import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Add, Assignment, MedicalServices, PlayArrow } from '@mui/icons-material';
import apiClient from '../lib/axios';

interface Template {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
  itemCount: number;
  createdAt: string;
  updatedAt: string;
}

export const TemplateList: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { data: templates, isLoading, error } = useQuery<Template[]>({
    queryKey: ['templates'],
    queryFn: async () => {
      const { data } = await apiClient.get<Template[]>('/templates');
      return data;
    },
  });

  if (isLoading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 4 }}>
          <Alert severity="error">{t('errors.failedToLoadTemplates')}</Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        <Paper sx={{ p: 4, mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Assignment sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
              <Box>
                <Typography variant="h4" component="h1" fontWeight="bold">
                  {t('doctor.templates.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('doctor.templates.subtitle')}
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => alert('Create template feature coming soon!')}
            >
              {t('doctor.templates.newTemplate')}
            </Button>
          </Box>
        </Paper>

        {!templates || templates.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <MedicalServices sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {t('doctor.templates.noTemplatesYet')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('doctor.templates.createFirstMessage')}
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => alert('Create template feature coming soon!')}
            >
              {t('doctor.templates.createFirstTemplate')}
            </Button>
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {templates.map((template) => (
              <Grid item xs={12} md={6} key={template.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="h6" fontWeight="bold">
                        {template.name}
                      </Typography>
                      <Chip
                        label={template.isActive ? t('common.active') : t('common.inactive')}
                        color={template.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </Box>

                    {template.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {template.description}
                      </Typography>
                    )}

                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Chip
                        label={`${template.itemCount} ${template.itemCount !== 1 ? t('doctor.templates.items') : t('doctor.templates.item')}`}
                        size="small"
                        variant="outlined"
                      />
                      <Typography variant="caption" color="text.secondary">
                        {t('common.updated')}: {new Date(template.updatedAt).toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'en-US')}
                      </Typography>
                    </Box>
                  </CardContent>
                  <CardActions sx={{ justifyContent: 'flex-end', p: 2, pt: 0 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<PlayArrow />}
                      onClick={() => navigate(`/doctor/orders/new?templateId=${template.id}`)}
                      disabled={!template.isActive}
                    >
                      {t('doctor.templates.useTemplate')}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </Container>
  );
};
